import { useState } from "react";
import Header from "../../commonComponents/header/Header";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import desktopLeftImg from "../../../assets/images/desktop-left-img.svg";
import needleEyeLogo from "../../../assets/images/moonshot-logo.svg";
import { useNavigate } from "react-router-dom";

const NeedleEyeDesktopIntroduction = () => {
  const navigate = useNavigate();
  const [showPanel, setShowPanel] = useState(false);

  const handleProceed = () => {
    navigate("/needleeye-desktop-setup");
  };

  return (
    <div className="flex flex-col min-h-screen bg-white overflow-x-hidden px-4 pt-4">
      <Header />

      {/* Main */}
      <div className="flex-1 flex flex-col md:flex-row items-center justify-center px-1 pt-5 max-w-9xl mx-auto">
        {/* LEFT IMAGE */}
        <div className="w-full md:w-1/2 flex justify-center items-center px-4 md:px-6 lg:px-8">
          <img
            src={desktopLeftImg}
            alt="NeedleEye Desktop"
            className="object-contain w-full max-w-[520px] h-auto"
          />
        </div>

        {/* Divider */}
        {!showPanel && (
          <div className="hidden md:block self-stretch w-[1px] bg-dividerColor mx-4 my-[5%]" />
        )}

        {/* RIGHT SECTION */}
        {showPanel ? (
          <div
            className="w-full md:w-1/2 flex flex-col mt-6 md:mt-0"
            style={{
              boxShadow:
                "0 4px 24px rgba(0,0,0,0.10), 0 1.5px 6px rgba(0,0,0,0.06)",

              border: "1px solid #F3F4F6",
              background: "#fff",
            }}
          >
            {/* HEADER */}
            <div className="flex justify-between items-start px-4 pt-4 pb-3 border-b">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <img
                    src={needleEyeLogo}
                    style={{ height: "50px", width: "auto" }}
                  />
                </div>
                <p className="text-[12px] text-gray-500">
                  NeedleEye Desktop – System-Level Monitoring for Interview
                  Integrity
                </p>
              </div>

              <button
                onClick={() => setShowPanel(false)}
                className="bg-[#002147] font-medium text-white px-3 py-1.5 text-[14px] rounded"
              >
                ✕ Close
              </button>
            </div>

            {/* SCROLLABLE CONTENT */}
            <div className="px-4 py-4 overflow-y-auto max-h-[55vh] pr-2 text-[16px] text-gray-700 leading-relaxed space-y-4">
              <p>
                NeedleEye Desktop is a lightweight, cross-platform desktop agent
                designed to monitor system-level behavior during AI interviews
                and assessments. Unlike browser-based solutions, it operates
                closer to the hardware layer—providing deeper visibility into
                device configurations and real-time changes that may indicate
                attempts to bypass interview/assessment controls.
              </p>

              <p>
                Available for Windows, macOS, and Linux, NeedleEye Desktop is
                installed by the candidate prior to the interview or assessment
                and runs as a secure background process throughout the session.
              </p>

              <p className="font-medium mt-4">How It Works</p>

              <p>
                After installation, the application is linked to the candidate’s
                session via a secure authentication flow.
              </p>

              <p>
                Once activated, it continuously monitors the system environment
                for changes that could compromise the integrity of the
                interview/assessment.
              </p>

              <p>During the session, the agent:</p>

              <ul className="list-disc ml-5 space-y-1">
                <li>Tracks connected hardware devices</li>
                <li>Detects real-time changes in system configuration</li>
                <li>Logs events and anomalies for post-session analysis</li>
                <li>
                  Optionally enforces environmental constraints based on policy
                </li>
              </ul>

              <p>
                All monitoring is performed locally with minimal resource usage,
                ensuring that interview performance is not affected.
              </p>

              <p className="font-medium mt-4">What It Monitors</p>

              <p>
                NeedleEye Desktop focuses on identifying signals that suggest
                external assistance or circumvention, including:
              </p>

              <p className="font-medium">Display & Screen Behavior</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Connection of external monitors or extended displays</li>
                <li>Screen duplication or mirroring attempts</li>
                <li>Sudden resolution or display topology changes</li>
              </ul>

              <p className="font-medium">Peripheral Devices</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>
                  Connection or disconnection of headphones, earbuds, or audio
                  devices
                </li>
                <li>
                  USB devices such as external keyboards, storage drives, or
                  input devices
                </li>
                <li>Bluetooth device pairing during the session</li>
              </ul>

              <p className="font-medium">System Environment</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Changes in active processes or system states</li>
                <li>
                  Suspicious background activity that may indicate auxiliary
                  tools
                </li>
                <li>
                  OS-level signals that suggest virtualization or remote control
                  usage
                </li>
              </ul>

              <p>
                The system maintains a timeline of events, enabling precise
                correlation with interview moments.
              </p>

              <p className="font-medium mt-4">
                Real-Time vs Post-Session Analysis
              </p>

              <p>
                While NeedleEye Desktop is capable of real-time detection, its
                primary strength lies in forensic analysis:
              </p>

              <ul className="list-disc ml-5 space-y-1">
                <li>Events are time-stamped and structured</li>
                <li>Correlated with interview responses and behavior</li>
                <li>Used to generate a malpractice risk profile</li>
              </ul>

              <p>
                This avoids unnecessary disruption during the interview while
                still ensuring robust oversight.
              </p>

              <p className="font-medium mt-4">Security & Privacy</p>

              <p>
                NeedleEye Desktop is engineered to strike a balance between
                monitoring and user trust:
              </p>

              <ul className="list-disc ml-5 space-y-1">
                <li>No access to personal files or unrelated data</li>
                <li>No screen recording unless explicitly enabled by policy</li>
                <li>
                  Monitoring is restricted to the interview session window
                </li>
                <li>All collected data is securely transmitted and stored</li>
              </ul>

              <p>
                Candidates are clearly informed about what is monitored and why,
                maintaining transparency throughout.
              </p>

              <p className="font-medium mt-4">Why It Matters</p>

              <p>
                Candidates have access to a wide range of tools that can subtly
                influence interview outcomes—secondary displays, hidden devices,
                or background assistance tools. Browser-based controls alone are
                insufficient to detect these.
              </p>

              <p>NeedleEye Desktop provides:</p>

              <ul className="list-disc ml-5 space-y-1">
                <li>Hardware-level visibility beyond the browser</li>
                <li>Detection of real-time environment manipulation</li>
                <li>Strong deterrence through awareness of monitoring</li>
              </ul>

              <p>
                For organizations, it ensures that hiring decisions are based on
                genuine capability—not augmented performance.
              </p>
            </div>
          </div>
        ) : (
          <div className="w-full md:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-10 mt-6 md:mt-0">
            <div className="max-w-[720px]">
              {/* LOGO */}
              <div className="w-full flex justify-center mb-6">
                <img src={needleEyeLogo} className="h-16 md:h-20" />
              </div>

              <p className="mb-4 text-[16px] md:text-[18px] lg:text-[22px] outer-font-color">
                <span className="font-medium">NeedleEye (Desktop) </span> is the
                companion app for{" "}
                <span className="font-medium">NeedleInTheHaystack</span> and is
                required to be installed on your computer.
              </p>

              <p className="mb-4 text-[16px] md:text-[18px] lg:text-[22px] outer-font-color">
                To know more about NeedleEye,{" "}
                <span
                  onClick={() => setShowPanel(true)}
                  className="text-blue-600 underline cursor-pointer"
                >
                  click here
                </span>
                .
              </p>

              <p className="text-[16px] md:text-[18px] lg:text-[22px] outer-font-color">
                Please click on <span className="font-medium">"Proceed"</span>{" "}
                for instructions to install and run{" "}
                <span className="font-medium">NeedleEye for Desktop.</span>
              </p>
            </div>
          </div>
        )}
      </div>
      {/* GLOBAL PROCEED BUTTON */}
      <div className="mt-auto w-full flex justify-end pb-4">
        <button
          onClick={handleProceed}
          className="btn-size flex bg-btnBgColor items-center px-6 py-1.5 text-white text-[16px]"
        >
          Proceed
          <img src={arrowImg} className="ml-2" />
        </button>
      </div>
      {/* Footer */}
      <div className="w-full py-4 text-[10px] text-gray-600 text-center px-4">
  © {new Date().getFullYear() > 2026 ? `2026-${new Date().getFullYear()}` : '2026'}, Needles and Haystacks Technology Pvt Ltd.
      </div>
    </div>
  );
};

export default NeedleEyeDesktopIntroduction;
